import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  Tooltip,
  ModalWrapper,
  Accordion,
  AccordionItem,
  TextInput,
  Link,
} from 'carbon-components-react';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import './TurbonomicROI.scss';
import './NoteDrawer.scss';
import { Close32, Launch32, DocumentPdf32 } from '@carbon/icons-react';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import NoteDrawer from './NoteDrawer';
import Thanks from './Thanks';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import {
  removeSymbol,
  useCurrency,
} from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from '../utils/swapCountryAndLanguage';
import {
  CurrentNumberOnPremHosts,
  limits,
  CurrentMonthtlyCloudBill,
  CurrentCostOnPremHost,
  AnnualGrowthRateOnPremVM,
  AnnualGrowthRateCloudInstance,
  UT30TurboROI,
} from '../constants/defaults';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setPrice as setROI } from '../hooks/useRoi';
import {
  determineCountry,
  LangDefaults,
  useLang,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';

import useTimer from 'src/common/hooks/useTimer';
import { getParamsFromUrl } from 'src/common/utils/getParamsFromUrl';
import { FormModal } from 'src/common/utils/FormModal';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { Helmet } from 'react-helmet';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';

export interface TurbonomicROIProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

/**
 *
 * @param {*} param0
 * @returns
 */
const TurbonomicROI = ({
  langCode,
  test,
  countryToTest,
}: TurbonomicROIProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);

  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [environment] = useEnv();
  const [currentNumberOnPremHosts, setCurrentNumberOnPremHosts] = useState(
    CurrentNumberOnPremHosts.default,
  );
  const [currentNumberOnPremHostsDefault, setCurrentNumberOnPremHostsDefault] =
    useState(CurrentNumberOnPremHosts.default);
  const [currentMonthtlyCloudBillDefault, setCurrentMonthtlyCloudBillDefault] =
    useState(CurrentMonthtlyCloudBill.default);
  const [currentMonthtlyCloudBill, setCurrentMonthtlyCloudBill] = useState(
    CurrentMonthtlyCloudBill.default,
  );
  const [currentCostOnPremHost, setCurrentCostOnPremHost] = useState(
    CurrentCostOnPremHost.default,
  );
  // Value obtained from turbonomic Price Estimator routine calculator.
  const [peNumberOfVMs, setPeNumberOfVMs] = useState(
    CurrentNumberOnPremHosts.default,
  );
  const [peCloudInstannces, setPeCloudInstannces] = useState(
    CurrentMonthtlyCloudBill.default,
  );
  //
  const [annualGrowthRateOnPremVM, setAnnualGrowthRateOnPremVM] = useState(
    AnnualGrowthRateOnPremVM.default,
  );
  const [annualGrowthRateCloudInstance, setAnnualGrowthRateCloudIntance] =
    useState(AnnualGrowthRateCloudInstance.default);
  const [showContactUs, setShowContactUs] = useState(true);
  const [invalidCOPHMax, setInvalidCOPHMax] = useState(false);
  const [invalidCOPHMin, setInvalidCOPHMin] = useState(false);
  const [invalidCMCBMax, setInvalidCMCBMax] = useState(false);
  const [invalidCMCBMin, setInvalidCMCBMin] = useState(false);
  const [invalidCPOPHMax, setInvalidCPOPHMax] = useState(false);
  const [invalidCPOPHMin, setInvalidCPOPHMin] = useState(false);
  const [
    invalidAnnualGrowthRateOnPremVMMax,
    setInvalidAnnualGrowthRateOnPremVMMax,
  ] = useState(false);
  const [
    invalidAnnualGrowthRateOnPremVMMin,
    setInvalidAnnualGrowthRateOnPremVMMin,
  ] = useState(false);
  const [
    invalidAnnualGrowthRateCloudIntanceMax,
    setInvalidAnnualGrowthRateCloudIntanceMax,
  ] = useState(false);
  const [
    invalidAnnualGrowthRateCloudIntanceMin,
    setInvalidAnnualGrowthRateCloudIntanceMin,
  ] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const { bpName, companyId, logo } = useBPName(UT30TurboROI, true);
  const [currency, setCurrency] = useState('');
  const [, setCountry] = useState('');
  //
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  // ROI
  const [rangeLow, setRangeLow] = useCurrency(currency, userLocale, 0);
  const [rangeHigh, setRangeHigh] = useCurrency(currency, userLocale, 0);
  //
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  const [moveSlider, setMoveSlider] = useState(false);
  const [valueSlider, setValueSlider] = useState(10000);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    false,
  );

  /**
   * Call to start the pricing
   */
  setROI(
    currentNumberOnPremHosts,
    currentMonthtlyCloudBill,
    currentCostOnPremHost,
    annualGrowthRateOnPremVM,
    annualGrowthRateCloudInstance,
    setShowContactUs,
    setRangeLow,
    setRangeHigh,
    peNumberOfVMs,
  );

  // for one mob detachment
  const code = getParamsFromUrl('code');

  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const ophId = document.getElementById('OPH-Id-input-for-slider');
    const mcbId = document.getElementById('MCB-Id-input-for-slider');
    const cpophId = document.getElementById('CPOPH-Id-input-for-slider');
    const opvId = document.getElementById('OPV-Id');
    const ciId = document.getElementById('CI-Id');
    /**
     * Define Listener
     * @param id
     * @param setMax
     * @param setMin
     * @param setvalue
     * @param max
     * @param min
     */
    const addListener = (
      id: HTMLElement | null,
      setMax: React.Dispatch<React.SetStateAction<boolean>>,
      setMin: React.Dispatch<React.SetStateAction<boolean>>,
      max: number,
      min: number,
      setvalue: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setMax(true);
                setvalue(max);
              } else if (Number.parseInt(element.value) < min) {
                element.value = min.toString();
                setvalue(min);
                setMin(true);
              }
            } else {
              element.value = ' ';
              setMin(true);
            }
            setMoveSlider(true);
          }
        });
      }
    };

    /**
     * Current number of on-premises host
     */
    addListener(
      ophId,
      setInvalidCOPHMax,
      setInvalidCOPHMin,
      CurrentNumberOnPremHosts.max,
      CurrentNumberOnPremHosts.min,
      setCurrentNumberOnPremHosts,
    );

    /**
     * Current monthly cloud bill
     */
    addListener(
      mcbId,
      setInvalidCMCBMax,
      setInvalidCMCBMin,
      CurrentMonthtlyCloudBill.max,
      CurrentMonthtlyCloudBill.min,
      setCurrentMonthtlyCloudBill,
    );

    /**
     * Current cost per on-premises host
     */
    addListener(
      cpophId,
      setInvalidCPOPHMax,
      setInvalidCPOPHMin,
      CurrentCostOnPremHost.max,
      CurrentCostOnPremHost.min,
      setCurrentCostOnPremHost,
    );

    /**
     * Annual growth rate on-prem VM
     */
    addListener(
      opvId,
      setInvalidAnnualGrowthRateOnPremVMMax,
      setInvalidAnnualGrowthRateOnPremVMMin,
      AnnualGrowthRateOnPremVM.max,
      AnnualGrowthRateOnPremVM.min,
      setAnnualGrowthRateOnPremVM,
    );

    /**
     * Annual growth rate cloud instance
     */
    addListener(
      ciId,
      setInvalidAnnualGrowthRateCloudIntanceMax,
      setInvalidAnnualGrowthRateCloudIntanceMin,
      AnnualGrowthRateCloudInstance.max,
      AnnualGrowthRateCloudInstance.min,
      setAnnualGrowthRateCloudIntance,
    );
  }, []);

  /**
   * Read and set defaults from PE
   */
  const startTxnProcessor = async () => {
    try {
      setTimeout(async () => {
        //
        const pe_OnPremVms = localStorage.getItem('VMS-Id');
        if (pe_OnPremVms !== null) {
          const value = Number.parseInt(pe_OnPremVms);
          if (value !== peNumberOfVMs) {
            setPeNumberOfVMs(value);
          }
        }
        //
        const pe_CloudInstances = localStorage.getItem('Cloud-Id');
        if (pe_CloudInstances !== null) {
          const value = Number.parseInt(pe_CloudInstances);
          if (value !== peCloudInstannces) {
            setPeCloudInstannces(value);
          }
        }
        startTxnProcessor();
      }, limits.secs * 1000);
    } catch (err) {
      console.error(`Error while timeout: ${err}`);
    }
  };

  startTxnProcessor();
  /**
   *
   */
  useEffect(() => {
    setCookie();
    // Updates ROI based on defaults if changed.
    const pe_OnPremVms = localStorage.getItem('VMS-Id');
    const pe_CloudInstances = localStorage.getItem('Cloud-Id');
    if (
      pe_OnPremVms !== null &&
      Number.parseInt(pe_OnPremVms) !== currentNumberOnPremHostsDefault
    ) {
      const value = Number.parseInt(pe_OnPremVms);
      setCurrentNumberOnPremHostsDefault(value);
      setCurrentNumberOnPremHosts(Math.round(value / 10));
    }
    if (
      pe_CloudInstances !== null &&
      Number.parseInt(pe_CloudInstances) !== currentMonthtlyCloudBillDefault
    ) {
      const value = Number.parseInt(pe_CloudInstances);
      setCurrentMonthtlyCloudBillDefault(value);
      setCurrentMonthtlyCloudBill(value * 100);
    }
  }, [
    currentNumberOnPremHosts,
    currentMonthtlyCloudBill,
    rangeLow,
    rangeHigh,
    currentCostOnPremHost,
    annualGrowthRateOnPremVM,
    annualGrowthRateCloudInstance,
    peNumberOfVMs,
    peCloudInstannces,
  ]);

  /**
   *
   */
  const setCookie = () => {
    const cookieContent =
      ' Price Estimator Values: Total number of on-premises VMs:' +
      peNumberOfVMs +
      ' Price Estimator Values: Total number of cloud instances needed:' +
      peCloudInstannces +
      ' | Current number of on-premises hosts:' +
      currentNumberOnPremHosts +
      ', ROI Values: Current monthly Cloud Bill: ' +
      currentMonthtlyCloudBill +
      ' | Current cost per on-premises hosts: ' +
      currentCostOnPremHost +
      ' | Annual growth rate on-premises VMs: ' +
      annualGrowthRateOnPremVM +
      ' | Annual growth rate cloud instances: ' +
      annualGrowthRateCloudInstance +
      ' | ROI: ' +
      rangeLow +
      ' - ' +
      removeSymbol(currency, rangeHigh) +
      ' | Request a quote -IBM Turbonmic ROI Estimator-' +
      ' | Referenced URL:' +
      window.location.href +
      ' | FormId:' +
      URX_FORM.FORM_ID;
    const text = JSON.stringify({
      source: URX_FORM.FORM_ID,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA: cookieContent,
        Q_ASSET_URL: window.location.href, // ISC - Asset URL
        Q_ASSET_NAME: 'IBM Turbonomic ROI Estimator', // ISC - Asset Name
      },
    });
    const jsonCookie = {
      message: cookieContent,
      content: {
        currentNumberOnPremHosts,
        currentMonthtlyCloudBill,
        currentCostOnPremHost,
        annualGrowthRateOnPremVM,
        annualGrowthRateCloudInstance,
        estimate: rangeLow + ' - ' + removeSymbol(currency, rangeHigh),
      },
    };
    localStorage.setItem('formContent', JSON.stringify(jsonCookie));
    Cookies.set('urxdcq', text);
  };

  /**
   *
   * @param value
   * @param setValue
   * @param max
   * @param min
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
    setMax: React.Dispatch<React.SetStateAction<boolean>>,
    setMin: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (value >= min && value <= max) {
      setValue(value);
      setMax(false);
      setMin(false);
    } else if (value > max) {
      setValue(max);
      setMax(true);
    } else {
      setMin(true);
      setValue(min);
    }
  };

  const { timeout, init } = useTimer(false, 120000);

  useEffect(() => {
    if (moveSlider && valueSlider != 10000) {
      init(true);
    }
  }, [valueSlider, moveSlider]);

  useEffect(() => {
    if (timeout) {
      setCookie();
      if (code === 'standalone') {
        initUrxForm(
          environment,
          URX_FORM.INSTANCE_ID,
          URX_FORM.FORM_ID,
          urxlang as IBMLocale,
          companyId,
        );
        setDrawerOpen(true);
      } else {
        localStorage.setItem('showPopupForm', 'true');
      }
      setMoveSlider(false);
    }
  }, [timeout]);

  return (
    <section id="Turbo_ROI" className="widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      <div className="Turbo_ROI dsh-widget widget-reset">
        {formComplete ? (
          <Thanks
            onPremVMs={peNumberOfVMs}
            cloudInstances={peCloudInstannces}
            currentNumberOnPremHosts={currentNumberOnPremHosts}
            currentMonthtlyCloudBill={currentMonthtlyCloudBill}
            rangeLow={rangeLow}
            rangeHigh={rangeHigh}
            currentCostOnPremHost={currentCostOnPremHost}
            annualGrowthRateOnPremVM={annualGrowthRateOnPremVM}
            annualGrowthRateCloudInstance={annualGrowthRateCloudInstance}
            currency={currency}
            bpName={bpName}
            bpConsentMail={false}
          ></Thanks>
        ) : (
          // Not Form completed
          <div className="bx--grid" id="TurboGrid">
            <div className="bx--row mar3">
              {/* <div className="bx--col-lg-4 bx--col-md-4"></div> */}
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                <div className="bx--row">
                  <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                    {localExist && toggleCountry ? (
                      <ModalWrapper
                        modalLabel={` ${getMessage(
                          messages.foundCountry,
                        )} ${modalLocalCountry}.
                          ${getMessage(
                            messages.accessPage,
                          )} ${modalLocalLanguage} ${modalLocalCurrency}`}
                        modalHeading={`${getMessage(messages.availableIn)}
                          ${modalLocalLanguage} ${getMessage(
                          messages.localCurrencie,
                        )} ${modalLocalCurrency}`}
                        size={'sm'}
                        primaryButtonText={`${getMessage(
                          messages.takeMeThere,
                        )}`}
                        secondaryButtonText={`${getMessage(
                          messages.stayHere,
                        )} `}
                        buttonTriggerText={` ${getMessage(
                          messages.accessPageCapitalized,
                        )} ${modalLocalLanguage}`}
                        handleOpen={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Turbonomic Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Modal open ' + modalLocalCountry,
                            location: 'ROI Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        handleSubmit={() => {
                          localStorage.setItem('langLocale', selectedLocale);
                          (window.location.href =
                            'https://www.ibm.com/' +
                            selectedLocale +
                            '/products/turbonomic/pricing'),
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Switched to country ' + modalLocalCountry,
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          return true;
                        }}
                        onSecondarySubmit={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Turbonomic Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA:
                              'Stay here button clicked ' + modalLocalCountry,
                            location: 'ROI Estimator',
                            action: '',
                            field: '',
                          });
                          setToggleCountry(false);
                          return true;
                        }}
                      ></ModalWrapper>
                    ) : null}
                  </div>
                </div>
                {/*  Current Number of on-prem Hosts */}
                <div className="bx--row">
                  <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_sub_heading">
                          <FormattedMessage {...messages.widgetText_1} />
                        </p>
                        <p className="page_body padding_24_3 mar7">
                          <FormattedMessage {...messages.widgetInfo} />
                          {` `}
                          <Link
                            href="https://www.g2.com/reports/enterprise-results-index-for-cloud-infrastructure-monitoring-spring-2023.embed?secure%5Bpid%5D=3640&secure%5Brid%5D=3037582&secure%5Btoken%5D=92bc2d326a6d2307e56e76cc7bfb8ad61763125bba900a25383480f2d55ea79b"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FormattedMessage {...messages.widgetInfo1} />
                            <Launch32 className="Turbo_ROI-load_icon" />
                          </Link>
                          {`. `}
                        </p>
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'On-premises hosts tooltip',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={isSmall ? 'bottom' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.labelNumberOnPremHosts}
                              />
                            </span>
                          }
                        >
                          <p className="slide_text">
                            <FormattedMessage
                              {...messages.numberOnPremHostsHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.numberOnPremHostsDescTooltip}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                    {/* Slider Current Number of on-prem Hosts */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="OPH-Id"
                          min={CurrentNumberOnPremHosts.min}
                          max={CurrentNumberOnPremHosts.max}
                          inputType={'number'}
                          value={currentNumberOnPremHosts}
                          invalid={invalidCOPHMax || invalidCOPHMin}
                          labelText={
                            invalidCOPHMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${CurrentNumberOnPremHosts.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidCOPHMin == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {` ${CurrentNumberOnPremHosts.min}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            handleSliders(
                              value,
                              setCurrentNumberOnPremHosts,
                              CurrentNumberOnPremHosts.max,
                              CurrentNumberOnPremHosts.min,
                              setInvalidCOPHMax,
                              setInvalidCOPHMin,
                            );

                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'On-premises hosts slider',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                            setMoveSlider(true);
                            setValueSlider(value);
                          }}
                        />
                      </div>
                    </div>
                    {/*  Monthly cloud bill Tooltip*/}
                    <div className="bx--row mar3">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Monthly cloud bill tooltip',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={isSmall ? 'left' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.currentMonthlyCloudBillLabel}
                              />
                            </span>
                          }
                        >
                          <p className="slide_text">
                            <FormattedMessage
                              {...messages.monthlyCloudBillHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.monthlyCloudBillDescTooltip}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                    {/*  Monthly cloud bill Slider */}
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="MCB-Id"
                          min={CurrentMonthtlyCloudBill.min}
                          max={CurrentMonthtlyCloudBill.max}
                          value={currentMonthtlyCloudBill}
                          inputType={'number'}
                          invalid={invalidCMCBMax || invalidCMCBMin}
                          labelText={
                            invalidCMCBMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${CurrentMonthtlyCloudBill.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidCMCBMin == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {` ${CurrentMonthtlyCloudBill.min}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            handleSliders(
                              value,
                              setCurrentMonthtlyCloudBill,
                              CurrentMonthtlyCloudBill.max,
                              CurrentMonthtlyCloudBill.min,
                              setInvalidCMCBMax,
                              setInvalidCMCBMin,
                            );
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Current monthly cloud bill slider',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                            setMoveSlider(true);
                            setValueSlider(value);
                          }}
                        />
                      </div>
                    </div>
                    {/*  Advanced Options*/}
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Accordion>
                          <AccordionItem
                            className="bx--row__com"
                            title="Advanced"
                          >
                            {/*  Current Cost per on-prem hosts Tooltip */}
                            <div className="bx--row mar3">
                              <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                                <Tooltip
                                  onChange={() => {
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Current cost per on-premises hosts tooltip',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                  direction={isSmall ? 'left' : 'bottom'}
                                  tabIndex={0}
                                  triggerText={
                                    <span className="page_body">
                                      <FormattedMessage
                                        {...messages.currentCostOnPremisesLabel}
                                      />
                                    </span>
                                  }
                                >
                                  <p className="slide_text">
                                    <FormattedMessage
                                      {...messages.currentCostOnPremisesHeadTooltip}
                                    />
                                    <br />
                                    <FormattedMessage
                                      {...messages.currentCostOnPremisesDescTooltip}
                                    />
                                  </p>
                                </Tooltip>
                              </div>
                            </div>
                            <div className="bx--row expand-slider">
                              <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                                {/*  Current Cost per on-prem hosts Slider */}
                                <Slider
                                  id="CPOPH-Id"
                                  min={CurrentCostOnPremHost.min}
                                  max={CurrentCostOnPremHost.max}
                                  value={currentCostOnPremHost}
                                  inputType={'number'}
                                  onBlur={() => {
                                    null;
                                  }}
                                  invalid={invalidCPOPHMax || invalidCPOPHMin}
                                  labelText={
                                    invalidCPOPHMax == true ? (
                                      <span className="slide_text">
                                        <FormattedMessage
                                          {...messages.invalidVmText_1}
                                        />
                                        {` ${CurrentCostOnPremHost.max}`}
                                        <FormattedMessage
                                          {...messages.invalidVmText_2}
                                        />
                                      </span>
                                    ) : invalidCPOPHMin == true ? (
                                      <span className="slide_text">
                                        <FormattedMessage
                                          {...messages.invalidVmText_3}
                                        />
                                        {` ${CurrentCostOnPremHost.min}`}
                                        <FormattedMessage
                                          {...messages.invalidVmText_2}
                                        />
                                      </span>
                                    ) : null
                                  }
                                  onChange={({ value }) => {
                                    handleSliders(
                                      value,
                                      setCurrentCostOnPremHost,
                                      CurrentCostOnPremHost.max,
                                      CurrentCostOnPremHost.min,
                                      setInvalidCPOPHMax,
                                      setInvalidCPOPHMin,
                                    );

                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Current cost per on-premises hosts slider',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    setMoveSlider(true);
                                    setValueSlider(value);
                                  }}
                                />
                              </div>
                            </div>
                            {/*  Advanced options */}
                            <div className="bx--row mar3">
                              {/*  Annual growth tooltip */}
                              <div className="bx--col-lg-16 bx--col-md-6 bx--col-sm-4">
                                <Tooltip
                                  onChange={() => {
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Annual growth tooltip',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                  direction={isSmall ? 'left' : 'bottom'}
                                  tabIndex={0}
                                  triggerText={
                                    <span className="page_body">
                                      <FormattedMessage
                                        {...messages.annualGrowthRateLabel}
                                      />
                                    </span>
                                  }
                                >
                                  <p className="slide_text">
                                    <FormattedMessage
                                      {...messages.annualGrowthRateHeadTooltip}
                                    />
                                    <br />
                                    <FormattedMessage
                                      {...messages.annualGrowthRateDescTooltip}
                                    />
                                  </p>
                                </Tooltip>
                              </div>
                            </div>
                            {/*  Annual growth */}
                            <div className="bx--row  mar6">
                              {/* Annual growth On-Prem tooltip */}
                              <div className="bx--col-lg-5 bx--col-md-4 bx--col-sm-4">
                                <Tooltip
                                  onChange={() => {
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Annual growth rate on-prem VMs tooltip',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                  direction={isSmall ? 'left' : 'bottom'}
                                  tabIndex={0}
                                  triggerText={
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.annualGrowthRateOnPremVMLabel}
                                      />
                                    </span>
                                  }
                                >
                                  <p className="slide_text">
                                    <FormattedMessage
                                      {...messages.annualGrowthRateOnPremVMLabel}
                                    />
                                    <br />
                                    <FormattedMessage
                                      {...messages.annualGrowthRateOnPremVMDescTooltip1}
                                    />
                                    {` ${AnnualGrowthRateOnPremVM.default}`}
                                    <FormattedMessage
                                      {...messages.annualGrowthRateOnPremVMDescTooltip2}
                                    />
                                  </p>
                                </Tooltip>
                                {/*On-prem VM Annual Grouth Rate text input */}
                                <TextInput
                                  id="OPV-Id"
                                  hideLabel={true}
                                  labelText={''}
                                  max={AnnualGrowthRateOnPremVM.max}
                                  min={AnnualGrowthRateOnPremVM.min}
                                  type={`number`}
                                  defaultValue={
                                    AnnualGrowthRateOnPremVM.default
                                  }
                                  invalid={
                                    invalidAnnualGrowthRateOnPremVMMax ||
                                    invalidAnnualGrowthRateOnPremVMMin
                                  }
                                  invalidText={
                                    invalidAnnualGrowthRateOnPremVMMax ==
                                    true ? (
                                      <>
                                        <FormattedMessage
                                          {...messages.invalidVmText_1}
                                        />
                                        {` ${AnnualGrowthRateOnPremVM.max}`}
                                        <FormattedMessage
                                          {...messages.invalidVmText_2}
                                        />
                                      </>
                                    ) : invalidAnnualGrowthRateOnPremVMMin ==
                                      true ? (
                                      <>
                                        <FormattedMessage
                                          {...messages.invalidVmText_3}
                                        />
                                        {` ${AnnualGrowthRateOnPremVM.min}`}
                                        <FormattedMessage
                                          {...messages.invalidVmText_2}
                                        />
                                      </>
                                    ) : null
                                  }
                                  onChange={(e) => {
                                    const regex = new RegExp('^[1-9]+$');
                                    setInvalidAnnualGrowthRateOnPremVMMin(
                                      !regex.test(e.target.value),
                                    );
                                    handleSliders(
                                      Number.parseInt(e.target.value),
                                      setAnnualGrowthRateOnPremVM,
                                      AnnualGrowthRateOnPremVM.max,
                                      AnnualGrowthRateOnPremVM.min,
                                      setInvalidAnnualGrowthRateOnPremVMMax,
                                      setInvalidAnnualGrowthRateOnPremVMMin,
                                    );
                                    setAnnualGrowthRateOnPremVM(
                                      Number.parseInt(e.target.value),
                                    );
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Annual growth rate on-prem VMs text input',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                ></TextInput>
                              </div>
                              {/*On-prem VM Annual Grouth Rate cloud instance */}
                              <div className="bx--col-lg-5 bx--col-md-4 bx--col-sm-4">
                                <Tooltip
                                  onChange={() => {
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Annual growth rate cloud instance tooltip',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                  direction={isSmall ? 'left' : 'bottom'}
                                  tabIndex={0}
                                  triggerText={
                                    <span className="slide_text">
                                      <FormattedMessage
                                        {...messages.annualGrowthRateCloudInstanceLabel}
                                      />
                                    </span>
                                  }
                                >
                                  <p className="slide_text">
                                    <FormattedMessage
                                      {...messages.annualGrowthRateCloudInstanceHeadTooltip}
                                    />
                                    <br />
                                    <FormattedMessage
                                      {...messages.annualGrowthRateCloudInstanceescTooltip1}
                                    />
                                    {` ${AnnualGrowthRateCloudInstance.default}`}
                                    <FormattedMessage
                                      {...messages.annualGrowthRateOnPremVMDescTooltip2}
                                    />
                                  </p>
                                </Tooltip>
                                {/*On-prem VM Annual Grouth Rate slideer  */}
                                <TextInput
                                  id="CI-Id"
                                  hideLabel={true}
                                  labelText={''}
                                  max={AnnualGrowthRateCloudInstance.max}
                                  min={AnnualGrowthRateCloudInstance.min}
                                  type={'number'}
                                  defaultValue={
                                    AnnualGrowthRateCloudInstance.default
                                  }
                                  invalid={
                                    invalidAnnualGrowthRateCloudIntanceMax ||
                                    invalidAnnualGrowthRateCloudIntanceMin
                                  }
                                  invalidText={
                                    invalidAnnualGrowthRateCloudIntanceMax ==
                                    true ? (
                                      <>
                                        <FormattedMessage
                                          {...messages.invalidVmText_1}
                                        />
                                        {` ${AnnualGrowthRateCloudInstance.max}`}
                                        <FormattedMessage
                                          {...messages.invalidVmText_2}
                                        />
                                      </>
                                    ) : invalidAnnualGrowthRateCloudIntanceMin ==
                                      true ? (
                                      <>
                                        <FormattedMessage
                                          {...messages.invalidVmText_3}
                                        />
                                        {` ${AnnualGrowthRateCloudInstance.min}`}
                                        <FormattedMessage
                                          {...messages.invalidVmText_2}
                                        />
                                      </>
                                    ) : null
                                  }
                                  onChange={(e) => {
                                    handleSliders(
                                      Number.parseInt(e.target.value),
                                      setAnnualGrowthRateCloudIntance,
                                      AnnualGrowthRateCloudInstance.max,
                                      AnnualGrowthRateCloudInstance.min,
                                      setInvalidAnnualGrowthRateCloudIntanceMax,
                                      setInvalidAnnualGrowthRateCloudIntanceMin,
                                    );
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'Turbonomic Pricing Page',
                                      object: 'Pricing Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Annual growth rate cloud instance text input',
                                      location: 'ROI Estimator',
                                      action: '',
                                      field: '',
                                    });
                                  }}
                                ></TextInput>
                              </div>
                            </div>
                            {/*  */}
                          </AccordionItem>
                        </Accordion>
                      </div>
                    </div>
                  </div>
                  {/* Navigation displaying results  */}
                  <div className="bx--col-lg-1"></div>
                  <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row" id="stickyNav">
                      <div className="Turbo_ROI__sticky-nav price_table_comp">
                        {/* Condition if above ceratin value to conta-us button */}
                        {showContactUs == true ? (
                          <div id="contact_toggle">
                            <p
                              id="widget.contact_info"
                              className="price_table_comp__disclaimer"
                            >
                              {currentNumberOnPremHosts +
                                currentMonthtlyCloudBill >
                              limits.max ? (
                                <FormattedMessage {...messages.contactInfo2} />
                              ) : (
                                <FormattedMessage {...messages.contactInfo} />
                              )}
                            </p>
                            <ButtonSet
                              className="Turbo_ROI__stack-button btn-set mar4"
                              stacked
                            >
                              <Button
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'Turbonomic Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Contact us',
                                    location: 'ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setCookie();
                                  localStorage.setItem('showForm', 'true');
                                }}
                              >
                                <FormattedMessage {...messages.contactUs} />
                              </Button>
                            </ButtonSet>
                          </div>
                        ) : (
                          //Display Value if condition not met
                          <div id="Turbo_ROI.price_toggle">
                            <p
                              id="Turbo_ROI.estimated-cost"
                              className="Turbo_ROI__ibm-type-e price_table_comp__bodycopy"
                            >
                              <FormattedMessage
                                {...messages.calculationMessage}
                              />
                              <sup className="Turbo_ROI__sup">3</sup>
                            </p>
                            <p className="Turbo_ROI__prices padding_8 price_table_comp__price">
                              {`${rangeLow} - ${removeSymbol(
                                currency,
                                rangeHigh,
                              )}`}
                              <sup className="Turbo_ROI__sup">2</sup>
                            </p>
                            <ButtonSet
                              className="Turbo_ROI__stack-button btn-set mar4"
                              stacked
                            >
                              <Button
                                id="request_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'Turbonomic Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Request a quote now',
                                    location: 'ROI Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setCookie();
                                  if (code === 'standalone') {
                                    initUrxForm(
                                      environment,
                                      URX_FORM.INSTANCE_ID,
                                      URX_FORM.FORM_ID,
                                      urxlang as IBMLocale,
                                      companyId,
                                    );
                                    setDrawerOpen(true);
                                  } else {
                                    localStorage.setItem('showForm', 'true');
                                  }
                                }}
                              >
                                <FormattedMessage
                                  {...messages.getQuoteButton}
                                />
                              </Button>
                            </ButtonSet>
                          </div>
                        )}
                        {/* End display results */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Note related to suggested price  */}
            </div>
          </div>
        )}
        {code === 'standalone' ? (
          <FormModal
            pageTitle="Turbonomic PE Page"
            object="Turbonomic PE Page"
            productTitle={'Turbonomic PE'}
            pageUrl={''}
            location={'Estimator'}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
          ></FormModal>
        ) : null}
      </div>
    </section>
  );
};

export default TurbonomicROI;
